import { useEffect, useState } from 'react'
import { FormSheet, OfficialUseBlock, SectionBand } from '../components/FormSheet'
import { RulingDocket } from '../components/RulingDocket'
import { StaffSignIn } from '../components/StaffSignIn'
import { setIntake as putIntake } from '../lib/admin'
import { isUnauthorized } from '../lib/apiFetch'
import { officialTimestamp } from '../lib/complaints'
import { getIntake, type Intake } from '../lib/intake'
import { currentStaff, signOut, type Staff } from '../lib/session'

/**
 * The department's own paperwork: the switch that opens and closes the
 * complaint window, and the docket it rules on.
 *
 * The path is still unlisted, but the path is no longer what protects it: every
 * endpoint behind it requires a signed-in staff member, so forwarding the link
 * now leaks a URL rather than the department.
 */
export function AdminPage() {
  const [staff, setStaff] = useState<Staff | null>(null)
  const [checkingSession, setCheckingSession] = useState(true)
  const [intake, setIntake] = useState<Intake | null>(null)
  const [notice, setNotice] = useState('')
  const [working, setWorking] = useState(false)

  // Who is signed in decides which page this is, so it is settled first. A 401
  // is the ordinary answer for a visitor, not an error worth showing.
  useEffect(() => {
    let current = true
    currentStaff()
      .then((me) => current && setStaff(me))
      .catch(() => {})
      .finally(() => current && setCheckingSession(false))
    return () => {
      current = false
    }
  }, [])

  useEffect(() => {
    if (!staff) return
    let current = true
    getIntake()
      .then((state) => current && setIntake(state))
      .catch(
        (error) =>
          current && setNotice(error instanceof Error ? error.message : 'The register could not be read.'),
      )
    return () => {
      current = false
    }
  }, [staff])

  /** A session that has died mid-use puts the door back in front of the page. */
  function onSessionLost() {
    setStaff(null)
    setIntake(null)
    setNotice('')
  }

  async function endSession() {
    try {
      await signOut()
    } finally {
      onSessionLost()
    }
  }

  async function change(accepting: boolean) {
    if (working) return
    setWorking(true)
    setNotice('')
    try {
      setIntake(await putIntake(accepting))
    } catch (error) {
      if (isUnauthorized(error)) {
        onSessionLost()
        return
      }
      setNotice(error instanceof Error ? error.message : 'The change could not be recorded.')
    } finally {
      setWorking(false)
    }
  }

  if (checkingSession) {
    return (
      <FormSheet form="Form DC-99 (Internal)" control="Departmental use only">
        <div className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Checking identification…
        </div>
      </FormSheet>
    )
  }

  if (!staff) {
    return <StaffSignIn onSignedIn={setStaff} />
  }

  return (
    <FormSheet form="Form DC-99 (Internal)" control="Departmental use only">
      <SignedInBand staff={staff} onSignOut={endSession} />
      <SectionBand title="Control of the complaint window" />

      {notice && (
        <p
          role="alert"
          className="mx-4 mt-5 border-l-4 border-stamp bg-stamp/5 px-3 py-2 font-form text-sm font-semibold text-stamp sm:mx-7"
        >
          {notice}
        </p>
      )}

      {!intake ? (
        <p className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Consulting the register…
        </p>
      ) : (
        <>
          <div className="px-4 pb-6 pt-5 sm:px-7">
            <p
              id="intake-switch-label"
              className="font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
            >
              Accept complaints
            </p>
            <p className="mt-0.5 font-form text-[11px] italic leading-snug text-carbon/60">
              While this is off, Form DC-1 is withdrawn and the public is shown a notice of closure.
            </p>

            <div className="mt-4 flex items-center gap-4">
              <IntakeSwitch
                accepting={intake.accepting}
                disabled={working}
                onChange={change}
                labelledBy="intake-switch-label"
              />
              <p className="font-type text-[17px] font-bold uppercase tracking-[0.08em] text-carbon">
                {intake.accepting ? 'Open' : 'Closed'}
              </p>
            </div>
          </div>

          {/* On DC-1 this block is hatched and forbidden. This is the department's
              own form, so the department writes in it. */}
          <OfficialUseBlock>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              <Entry label="Window" value={intake.accepting ? 'Open to filings' : 'Closed to filings'} />
              <Entry
                label="Interval quoted to the public"
                value={intake.reopenEstimate ?? 'None while open'}
              />
              <Entry
                label="Last changed"
                value={intake.updatedAt ? officialTimestamp(intake.updatedAt) : 'Never changed'}
              />
            </dl>
          </OfficialUseBlock>

          <div className="border-t border-carbon/20 px-4 py-5 sm:px-7">
            <p className="font-form text-[11px] leading-relaxed text-carbon/70">
              Closing the window quotes an interval to check back in, drawn once and printed on the
              notice until the window reopens. Closing a window already closed does not revise the
              interval already quoted. Complaints already on file remain retrievable by case number
              throughout, and may still be ruled on below.
            </p>
          </div>

          <RulingDocket onSessionLost={onSessionLost} />
        </>
      )}
    </FormSheet>
  )
}

/** Who is on duty, and the way out. */
function SignedInBand({ staff, onSignOut }: { staff: Staff; onSignOut: () => void }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-carbon/20 px-4 py-2 sm:px-7">
      <p className="font-form text-[10px] uppercase tracking-[0.16em] text-carbon/60">
        On duty: <span className="font-type normal-case tracking-normal text-carbon">{staff.emailAddress}</span>
      </p>
      <button
        type="button"
        onClick={onSignOut}
        className="font-form text-[10px] font-semibold uppercase tracking-[0.18em] text-process underline underline-offset-4 hover:text-carbon"
      >
        Sign out
      </button>
    </div>
  )
}

/**
 * A switch on a panel: it changes position, it does not animate. The one
 * orchestrated moment on this site is the stamp landing on a receipt.
 */
function IntakeSwitch({
  accepting,
  disabled,
  onChange,
  labelledBy,
}: {
  accepting: boolean
  disabled: boolean
  onChange: (accepting: boolean) => void
  labelledBy: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={accepting}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange(!accepting)}
      className={`relative h-9 w-[4.25rem] shrink-0 border-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        accepting ? 'border-process bg-process' : 'border-carbon/50 bg-carbon/10'
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-[3px] h-[26px] w-[26px] border bg-file ${
          accepting ? 'left-[2.125rem] border-process' : 'left-[3px] border-carbon/50'
        }`}
      />
    </button>
  )
}

function Entry({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-dotted border-carbon/25 pb-1.5">
      <dt className="font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">{label}</dt>
      <dd className="mt-0.5 font-type text-[14px] text-carbon">{value}</dd>
    </div>
  )
}
